// Видимость очереди записи PowerSync + toast при отброшенных операциях.
//
// Здесь ЕДИНСТВЕННЫЙ опрос `pendingUploadCount()` в приложении: результат
// уезжает в общий счётчик `setPendingSyncCount("powersync", n)`, откуда его
// читают и баннеры, и `usePowerSyncStatus`. Второй поллер в хуке статуса убран
// (он же давал двойной счёт).

import { toast } from "@studyhub/ui/toast";
import { Link } from "@tanstack/react-router";
import { type ReactNode, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useFeatureFlag } from "@/lib/observability/feature-flags";
import { setPendingSyncCount } from "@/lib/pending-sync-count";
import { pendingUploadCount } from "@/lib/powersync/client";
import { type DroppedOperation, SYNC_OPS_DROPPED_EVENT } from "@/lib/powersync/upload-failures";

const POLL_INTERVAL_MS = 5_000;

export function SyncQueueProvider({ children }: { children: ReactNode }) {
	const { t } = useTranslation("common");
	const recoveryCenterEnabled = useFeatureFlag("sync-recovery-center-enabled");

	useEffect(() => {
		let cancelled = false;
		let timer: number | null = null;

		const refresh = async (): Promise<void> => {
			const count = await pendingUploadCount().catch(() => 0);
			if (!cancelled) setPendingSyncCount("powersync", count);
		};

		// Опрос только на видимой вкладке. `getCrudBatch(100)` в Capacitor — это
		// сериализация через нативный мост; в фоне она круглосуточно жгла батарею,
		// не влияя ни на что: индикатор всё равно никто не видит.
		const stop = (): void => {
			if (timer !== null) window.clearInterval(timer);
			timer = null;
		};
		const start = (): void => {
			if (timer !== null) return;
			void refresh();
			timer = window.setInterval(() => void refresh(), POLL_INTERVAL_MS);
		};
		const onVisibility = (): void => {
			if (document.visibilityState === "visible") start();
			else stop();
		};

		onVisibility();
		document.addEventListener("visibilitychange", onVisibility);
		return () => {
			cancelled = true;
			stop();
			document.removeEventListener("visibilitychange", onVisibility);
			setPendingSyncCount("powersync", 0);
		};
	}, []);

	useEffect(() => {
		const onDropped = (event: Event): void => {
			const detail = (event as CustomEvent<DroppedOperation[]>).detail ?? [];
			if (detail.length === 0) return;
			if (recoveryCenterEnabled) {
				toast.error(t("offline.changesDroppedWithCenter", { count: detail.length }), {
					// `<Link>`, а не `window.location.assign`: полная перезагрузка SPA
					// посреди синхронизации сбрасывала бы кэш и очередь в памяти, а в
					// Capacitor означала холодный старт WebView (frontend-quality §11.2).
					action: (
						<Link
							to="/settings/offline"
							className="cursor-pointer text-xs font-medium underline-offset-2 hover:underline"
							onClick={() => toast.dismiss()}
						>
							{t("offline.syncIssuesAction")}
						</Link>
					),
				});
				return;
			}
			toast.error(t("offline.changesDropped", { count: detail.length }));
		};
		window.addEventListener(SYNC_OPS_DROPPED_EVENT, onDropped);
		return () => window.removeEventListener(SYNC_OPS_DROPPED_EVENT, onDropped);
	}, [recoveryCenterEnabled, t]);

	return <>{children}</>;
}
